import React from 'react';
import PropTypes from 'prop-types';
import './MultiSelectDropdown.css';
import SharedSearchInput from '../../../../../components/shared/SharedSearchInput/SharedSearchInput';

/**
 * MultiSelectDropdown - Ein generischer Filter-Dropdown für das Export-Modal.
 * Unterstützt Suche, Batch-Aktionen (Alle/Keine) und individuelle Auswahl.
 * 
 * @param {Object} props
 * @param {string} props.id - Die ID des Dropdowns
 * @param {string} props.label - Der Text auf dem Button
 * @param {Array} props.selected - Array der gewählten IDs
 * @param {Array} props.options - Array der verfügbaren Optionen [{id, name}]
 * @param {boolean} props.isOpen - Ob das Dropdown offen ist
 * @param {Function} props.onToggle - Callback zum Öffnen/Schließen
 * @param {Function} props.onChange - Callback bei Auswahl-Änderung
 * @param {string} props.searchTerm - Der aktuelle Suchbegriff
 * @param {Function} props.onSearchChange - Callback bei Sucheingabe
 */
const MultiSelectDropdown = ({ 
    id, 
    label, 
    selected, 
    options, 
    isOpen, 
    onToggle, 
    onChange, 
    searchTerm, 
    onSearchChange 
}) => {
    const isAllSelected = selected.length === options.length && options.length > 0;
    const isIndeterminate = selected.length > 0 && !isAllSelected;

    const filteredOptions = options.filter(opt => 
        (opt.name || '').toLowerCase().includes((searchTerm || '').toLowerCase())
    );

    const handleSelectAllFiltered = () => {
        const next = [...new Set([...selected, ...filteredOptions.map(o => o.id)])];
        onChange(next);
    };

    const handleSelectIndividual = (optId) => {
        const next = selected.includes(optId)
            ? selected.filter(id => id !== optId)
            : [...selected, optId];
        onChange(next);
    };

    return (
        <div className={`export-filter-dropdown ${isOpen ? 'open' : ''}`}>
            <button 
                className={`export-filter-btn ${isIndeterminate || isAllSelected ? 'has-selection' : ''}`}
                onClick={(e) => { e.stopPropagation(); onToggle(isOpen ? null : id); }}
            >
                <span className="export-filter-label">{label}</span>
                <span className="export-filter-count">
                    {selected.length === options.length ? 'Alle' : selected.length}
                </span>
                <span className="export-filter-chevron">▼</span>
            </button>

            {isOpen && (
                <div className="export-dropdown-panel" onClick={(e) => e.stopPropagation()}>
                    <div className="export-dropdown-search">
                        <SharedSearchInput 
                            placeholder="Suchen..." 
                            value={searchTerm}
                            onChange={(e) => onSearchChange(e.target.value)}
                            onClear={() => onSearchChange('')}
                        />
                    </div>
                    
                    <div className="export-dropdown-list">
                        {filteredOptions.length === 0 ? (
                            <div className="export-dropdown-empty">Keine Treffer</div>
                        ) : (
                            filteredOptions.map(opt => (
                                <div 
                                    key={opt.id} 
                                    className={`export-dropdown-item ${selected.includes(opt.id) ? 'selected' : ''}`}
                                    onClick={() => handleSelectIndividual(opt.id)}
                                >
                                    <div className={`custom-checkbox mini ${selected.includes(opt.id) ? 'checked' : ''}`}>
                                        {selected.includes(opt.id) ? '✓' : ''}
                                    </div>
                                    {opt.color && (
                                        <span 
                                            className="export-opt-dot" 
                                            style={{ backgroundColor: opt.color, width: 8, height: 8, borderRadius: '50%', marginRight: 8 }} 
                                        />
                                    )}
                                    {opt.name}
                                </div>
                            ))
                        )}
                    </div>

                    <div className="export-dropdown-actions">
                        <button 
                            className="export-action-btn" 
                            onClick={searchTerm ? handleSelectAllFiltered : () => onChange(options.map(o => o.id))}
                        >
                            {searchTerm ? 'Diese Treffer' : 'Alle wählen'}
                        </button>
                        <button 
                            className="export-action-btn secondary" 
                            onClick={() => onChange([])}
                        >
                            Leeren
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

MultiSelectDropdown.propTypes = {
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    selected: PropTypes.array.isRequired,
    options: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        name: PropTypes.string.isRequired
    })).isRequired,
    isOpen: PropTypes.bool.isRequired,
    onToggle: PropTypes.func.isRequired,
    onChange: PropTypes.func.isRequired,
    searchTerm: PropTypes.string,
    onSearchChange: PropTypes.func.isRequired
};

export default MultiSelectDropdown;
